import {BaseElementAsync, MsgLabel, OSkinableInit} from "/@back@/commons/basis.js";
import {IWspEnv, JSkinSetEntry, JSkinSets, JWspTypeInst, WSP} from "/@lib@/wsp/wsp.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {IPendingValidity, MxFormElement} from "/@lib@/commons/forms.js";
import {GridSmall, OGridSmallInit} from "/@back@/commons/widgets/grid-tags.js";
import {GridDataHolderJsonTree} from "/@back@/commons/widgets/tree.js";
import {CellBuilderIconLabel, CellBuilderString, GridDataRowJson} from "/@back@/commons/widgets/grid-libs.js";
import {GridColDef, ICellBuilder, IGridDataRow, IGridLineDrawer} from "/@back@/commons/widgets/grid-core.js";

export interface OSkinSetSelectorInit extends OSkinableInit {
	reg: IReg<IWspEnv>
	disabled?: boolean
	propsJsonName?: string
}

export interface IWspTypeDefMaker {
	fillWspTypeInst(): JWspTypeInst;
}


export class SkinSetSelector extends MxFormElement(BaseElementAsync) implements IGridLineDrawer, IPendingValidity {

	protected config: OSkinSetSelectorInit;

	protected reg: IReg<IWspEnv>;

	protected rootConfElt: HTMLFormElement;

	protected treeElt: GridSmall;

	protected msgElt: MsgLabel;

	protected async _initialize(init: OSkinSetSelectorInit): Promise<any> {
		this.config = init;
		this.reg = this.findReg(init);
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);

		this.msgElt = sr.appendChild(<MsgLabel/>) as MsgLabel;
		this.rootConfElt = sr.appendChild(<form name="skinset"/>) as HTMLFormElement;

		this.tabIndex = -1;

		this.rootConfElt.appendChild(
			<div>
				<div class="choice">
					<input type="radio" name="editMode" value="white" id="editModeWhite" onchange={this.onChangeEditMode}/>
					<label for="editModeWhite" title="Inclure uniquement les éléments sélectionnés">Liste d'inclusions</label>
				</div>
			</div>);
		this.rootConfElt.appendChild(
			<div>
				<div class="choice">
					<input type="radio" name="editMode" value="black" id="editModeBlack" onchange={this.onChangeEditMode}/>
					<label for="editModeBlack" title="Tout inclure sauf les éléments sélectionnés">Liste d'exclusions</label>
				</div>
			</div>
		);

		this.treeElt = this.rootConfElt.appendChild(<GridSmall id="tree" î={{
			reg: this.reg,
			columnDefs: [
				new GridColDef('selected').setMaxWidth("2em").setMinWidth("2em").setCellBuilder(new CellBuilderSkinSelected("selected")),
				new GridColDef('icon').setMinWidth('calc(var(--icon-size) * 2)').setFlex("unset").setCellBuilder(new CellBuilderSkinImage()),
				new GridColDef('title').setFlex('1rem', 1, 1).setMinWidth('10em')
					.setCellBuilder(new CellBuilderIconLabel("title").setIconWidth("0px").setDescriptionFunc((row: IGridDataRow) => {
						let version = row.getData("version");
						return `Version ${version}`;
					})),
			],
			lineDrawer: this,
			hideHeaders: true,
			selType: 'monoClick',
			skinScroll: 'scroll/small',
			skinOver: 'skinset-grid',
			noResizableCol: true,
			dataHolder: new GridDataHolderJsonTree<JSkinSetEntry>(),
			emptyBody: <div style="font-style:italic">Aucun habillage graphique disponible</div>
		} as OGridSmallInit} c-resizable=""/>) as GridSmall;

		this.treeElt.uiEvents.on('rowDblclick', (row: GridDataRowJson<JSkinSetEntry> | null, ev: MouseEvent) => {
			if (!this.disabled)
				if ((ev.target as Node).nodeName != 'input') {
					let line = this.treeElt._getLineFrom(ev.target as Node);
					if (line)
						line.querySelector("input").click();
				}
		});

		this.treeElt.addEventListener('keypress', (ev: KeyboardEvent) => {
			if (ev.key === ' ' || ev.key === 'Enter') {
				let line = this.treeElt._getLine(this.treeElt.getSelectedRow());
				if (line)
					line.querySelector("input").click();
			}
		});

		this.addEventListener("change", this.refreshValidity);

		this._initializeForm(init);
		await this._refreshAll();
	}

	/**
	 * Fonctions de refresh UI
	 */

	protected refreshUi() {
		if (this.initStatus != "pending") {
			this.reinitializeStarted(this._refreshAll());
			this.refreshValidity();
		}
	}

	public refreshValidity() {
		let skinsets = (this.treeElt.dataHolder as GridDataHolderJsonTree<JSkinSetEntry>).getDatas();
		if (this.editMode === "white" && !skinsets?.find((entry => entry.selected)))
			this.setValidity({valueMissing: true}, "Veuillez inclure au moins un habillage graphique");
		else if (this.editMode === "black" && !skinsets?.find((entry => !entry.selected)))
			this.setValidity({valueMissing: true}, "Veuillez ne pas exclure au moins un habillage graphique");
		else
			this.setValidity({}, "");
	}

	dispatchChangeEvent() {
		this.dispatchEvent(new CustomEvent("change", {cancelable: true, bubbles: true}));
	}

	refreshPending: Promise<JSkinSets>;

	protected async _refreshAll(): Promise<void> {
		this.addPendingValidity(this);
		this.msgElt.setStandardMsg("loading");
		try {
			// - Load de la liste des skins avec l'état courant
			this.refreshPending = WSP.skinSetsList(this.reg.env.wsp, this, document.documentElement.lang);
			// - Clean
			(this.treeElt.dataHolder as GridDataHolderJsonTree<JSkinSetEntry>).setDatas([]);
			let skinSets: JSkinSets = await this.refreshPending;
			// - affichage UI
			this.editMode = skinSets.editMode;
			(this.treeElt.dataHolder as GridDataHolderJsonTree<JSkinSetEntry>).setDatas(skinSets.skinSets);
			this.msgElt.setStandardMsg(null);
			this.refreshValidity();
		} catch (e) {
			this.msgElt.setCustomMsg("Chargement impossible", "error");
			await ERROR.log(e);
		} finally {
			this.refreshPending = null;
		}
	}

	protected updateState(skins? : ["white" | "black", ...String[]]) {
		if (skins?.length >= 1) {
			const currentDatas : JSkinSetEntry[] = (this.treeElt.dataHolder as GridDataHolderJsonTree<JSkinSetEntry>).getDatas();
			this.editMode = skins[0];
			currentDatas.forEach(entry => entry.selected = skins.includes(entry.code, 1));
			(this.treeElt.dataHolder as GridDataHolderJsonTree<JSkinSetEntry>).setDatas(currentDatas);
		}
	}

	async computeValidity(): Promise<void> {
		await this.refreshPending;
	}

	formDisabledCallback(val: boolean) {
		(this.rootConfElt.querySelector("#editModeWhite") as HTMLInputElement).disabled = val;
		(this.rootConfElt.querySelector("#editModeBlack") as HTMLInputElement).disabled = val;
		let tree = this.rootConfElt.querySelector("#tree") as GridSmall;
		if (val) DOM.setAttr(tree, "disabled", "true");
		else tree.removeAttribute("disabled");
		this.refreshUi();//recalcul des cells avec input
	}

	protected onChangeEditMode(this: HTMLInputElement, ev: Event): void {
		const host: SkinSetSelector = DOMSH.findHost(this);
		host.editMode = this.value == "white" ? "white" : "black";
		host.dispatchChangeEvent();
	}

	redrawLine(row: GridDataRowJson<JSkinSetEntry>, line: HTMLElement): void {
		if (row.rowDatas.selected)
			DOM.addClass(line, "selected");
		else
			DOM.removeClass(line, "selected");
	}

	/* Retourne la logique de sélection courante */
	get editMode(): "white" | "black" | undefined {
		const tree = this.rootConfElt.querySelector("#tree") as GridSmall;
		if (tree.classList.contains("modewhite"))
			return "white"
		if (tree.classList.contains("modeblack"))
			return "black"
		return undefined
	}

	set editMode(val: "white" | "black") {
		const tree = this.rootConfElt.querySelector("#tree") as GridSmall;
		if (this.editMode != val) {
			let inputElt;
			if (val == "white") {
				DOM.addClass(tree, "modewhite");
				DOM.removeClass(tree, "modeblack");
				inputElt = this.rootConfElt.querySelector("#editModeWhite") as HTMLInputElement;
				inputElt.checked = true;
			} else {
				DOM.addClass(tree, "modeblack");
				DOM.removeClass(tree, "modewhite");
				inputElt = this.rootConfElt.querySelector("#editModeBlack") as HTMLInputElement;
				inputElt.checked = true;
			}
			// - Déplacement du tree
			inputElt.parentNode.parentNode.appendChild(tree);
			// - Inversion des états
			const dataHolder: GridDataHolderJsonTree<JSkinSetEntry> = this.treeElt.dataHolder as GridDataHolderJsonTree<JSkinSetEntry>;
			const datas = dataHolder.getDatas();
			datas.forEach((entry) => entry.selected = entry.selected ? false : true);
			dataHolder.setDatas(datas);
		}
	}

	/**
	 * Retourne skinSet construit
	 */
	fillSkinSet(): ["white" | "black", ...String[]] | null {
		let tree = this.rootConfElt.querySelector("#tree") as GridSmall;
		let result: ["white" | "black", ...String[]] = [this.editMode];
		let skinsets = (tree.dataHolder as GridDataHolderJsonTree<JSkinSetEntry>).getDatas();
		skinsets.forEach((skinset) => {
			if (skinset.selected) result.push(skinset.code)
		});
		return result;
	}

	extractJson(parent: Dict<any>): boolean {
		if(parent && parent[this.config.propsJsonName]) {
			this.updateState(parent[this.config.propsJsonName]);
			return true;
		}else
			return false;
	}

	fillJson(parent: any, root: any): void {
		if (this.config.propsJsonName) {
			parent[this.config.propsJsonName] = this.fillSkinSet();
		}
	}

}

customElements.define('skinset-selector', SkinSetSelector);
REG.reg.registerSkin('skinset-selector', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		overflow: auto;
		user-select: none;
		border: 1px solid transparent;
	}

	:host(:invalid) {
		border: 1px solid var(--error-color);
		box-shadow: 0 0 2px var(--error-color);
	}

	.choice {
		display: flex;
		align-items: center;
	}

	#tree {
		margin-top: .2em;
		margin-inline-start: 2em;
	}


`);

/** Skin du grid **/
REG.reg.registerSkin('skinset-grid', 1, /* language=CSS */ `
	:host(.modeblack) .line.selected,
	:host(.modewhite) .line:not(.selected) {
		text-decoration: line-through;
	}

	.colW {
		border-color: transparent !important;
	}

	.imgcell {
		text-align: center;
	}
	
	.line{
	  align-items: center;
	}

	img {
	  max-height: calc(var(--icon-size) * 2);
	  width: calc(var(--icon-size) * 2);
	}
`);

/** Cell builder checked*/
class CellBuilderSkinSelected extends CellBuilderString {

	redrawCell(row: IGridDataRow, root: HTMLElement): void {
		root.innerText = "";
		const grid = DOMSH.findHost(root) as GridSmall;
		const skinSetSelector = DOMSH.findHost(grid) as SkinSetSelector;
		const line = grid._getLineFrom(root);

		function onChangeSelectSkin(this: HTMLInputElement, ev: Event): void {
			const offset = grid.getActiveRow();
			let datas = (grid.dataHolder as GridDataHolderJsonTree<JSkinSetEntry>).getDataByOffset(offset);
			datas.selected = this.checked;
			grid.invalidateRows(offset, 1);
			skinSetSelector.dispatchChangeEvent();
			ev.preventDefault();
		}

		root.appendChild(<input type="checkbox" disabled={grid.getAttribute('disabled')} checked={this._getValue(row) === true ? true : null} onchange={onChangeSelectSkin}/>);
	}

}

/** Cell builder checked*/
class CellBuilderSkinImage implements ICellBuilder {
	getColSortFn(): ((r1: IGridDataRow, r2: IGridDataRow) => number) | null {
		return undefined;
	}

	redrawCell(row: IGridDataRow, root: HTMLElement): void {
		const grid = DOMSH.findHost(root) as GridSmall;
		const reg = REG.findReg(grid) as IReg<IWspEnv>;
		DOM.setHidden(root, false);
		if (!root.firstElementChild) {
			DOM.addClass(root, "imgcell");
			root.appendChild(<img onerror={function (this: HTMLImageElement, ev: Event) {
				DOM.setHidden(this, true);
			}}/>);
		}
		const img = root.firstElementChild as HTMLImageElement;
		img.src = WSP.skinSetsIconUrl(reg.env.wsp, row.getData("code"));
	}

}